import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

// Opening this page only INSPECTS the token (link-status). The token is burned
// solely by the explicit POST /auth/reset-password the user submits below.
export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { applyAuth } = useAuth();
  const nav = useNavigate();
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [hint, setHint] = useState("");
  const [why, setWhy] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setChecking(false); setValid(false); setWhy("This reset link is missing its token."); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/link-status?token=${encodeURIComponent(token)}`);
        if (data.status === "valid" && data.purpose === "reset") {
          setValid(true); setHint(data.email_hint || "");
        } else if (data.status === "used") setWhy("This reset link has already been used.");
        else if (data.status === "expired") setWhy(`This reset link has expired (links last ${data.minutes} minutes).`);
        else setWhy("This reset link is not valid.");
      } catch (e) {
        setWhy(apiError(e.response?.data?.detail) || "This reset link is not valid.");
      } finally { setChecking(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: pw });
      applyAuth(data.user ? { ...data.user, token: data.token } : data);
      toast.success("Password updated — you're signed in");
      nav("/");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const input = "w-full bg-white border border-[#D7DBE0] rounded-lg px-4 py-3 text-sm focus:border-[#C8000F] focus:ring-2 focus:ring-[#C8000F]/20 focus:outline-none transition";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0C1017] p-6" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl p-8 rp-fade-up">
        <img src="/rodeo-pilot-wordmark.webp" alt="Rodeo Pilot" className="h-14 w-auto object-contain mx-auto mb-6" />
        {checking ? (
          <div className="text-center py-6"><Loader2 className="w-8 h-8 animate-spin text-[#C8000F] mx-auto" /></div>
        ) : valid ? (
          <>
            <h1 className="font-display text-2xl font-extrabold text-center mb-1">Set a new password</h1>
            <p className="text-slate-500 text-sm text-center mb-6">for {hint}</p>
            <form onSubmit={submit} className="space-y-4">
              <input className={input} type="password" placeholder="New password" required minLength={6} autoComplete="new-password"
                data-testid="reset-new-password" value={pw} onChange={(e) => setPw(e.target.value)} />
              <input className={input} type="password" placeholder="Confirm new password" required minLength={6} autoComplete="new-password"
                data-testid="reset-confirm-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
              <button type="submit" disabled={saving} data-testid="reset-submit-btn"
                className="w-full bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold py-3 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Update password
              </button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="reset-error">
            <h1 className="font-display text-xl font-bold mb-2">Reset link problem</h1>
            <p className="text-slate-500 text-sm mb-6">{why}</p>
            <button onClick={() => nav("/login")} data-testid="reset-back-btn"
              className="w-full bg-[#C8000F] hover:bg-[#9E0008] text-white font-semibold py-3 rounded-lg">
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
